import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { subscribePartnerSchedulingAllowedQueues } from '../../api/partner-scheduling-workbook-subscriptions.js';
import { PartnerSchedulingAllowedQueue } from '@nsc/workflows-partner-scheduling/entities/PartnerSchedulingAllowedQueue.js';
import { html, nothing } from 'lit';

export const PartnerSchedulingAllowedQueuesSection = stateful((props: { canManage: boolean }) => {
  const [queueInput, setQueueInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queues = useObservable(
    () =>
      subscribePartnerSchedulingAllowedQueues()({
        _id: 1,
        _class: 1,
        queueName: 1,
      }),
    [],
  ) as PartnerSchedulingAllowedQueue[];

  const sortedQueues = [...(queues ?? [])].sort((a, b) => (a.queueName ?? '').localeCompare(b.queueName ?? ''));

  const addQueue = async () => {
    const queueName = queueInput.trim();
    if (!queueName) {
      setError('Enter an OTRS queue name.');
      return;
    }

    const exists = (queues ?? []).some(row => (row.queueName ?? '').trim() === queueName);
    if (exists) {
      setError('This queue is already in the list.');
      return;
    }

    setError(null);
    const row = new PartnerSchedulingAllowedQueue({ queueName });
    await row.create();
    setQueueInput('');
  };

  return html`
    <section class="card bg-base-100 shadow-xl rounded-2xl border border-neutral/10">
      <div class="card-body gap-4">
        <h2 class="card-title text-lg font-bold text-neutral">Allowed OTRS queues</h2>
        <p class="text-base text-base-content/70">
          Partner scheduling only runs for tickets in these queues (exact match against OTRS ticket
          <span class="font-semibold">Queue</span>). When the list is empty, nothing triggers.
        </p>
        ${props.canManage
          ? html`
              <div class="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
                <label class="form-control w-full max-w-xl">
                  <span class="label-text font-medium">Queue name</span>
                  <input
                    type="text"
                    class="input input-bordered"
                    placeholder="e.g. Projects::Dell"
                    .value=${queueInput}
                    @input=${(e: Event) => setQueueInput((e.target as HTMLInputElement).value)}
                    @keydown=${(e: KeyboardEvent) => {
                      if (e.key === 'Enter') void addQueue();
                    }}
                  />
                </label>
                <button type="button" class="btn btn-primary btn-sm" @click=${() => void addQueue()}>Add queue</button>
              </div>
              ${error ? html`<p class="text-sm text-error">${error}</p>` : nothing}
            `
          : html`<p class="text-sm text-warning">
              View only — managing queues requires Partner Workbook Import permission.
            </p>`}
        <div class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Queue</th>
                ${props.canManage ? html`<th class="w-24"></th>` : nothing}
              </tr>
            </thead>
            <tbody>
              ${sortedQueues.length === 0
                ? html`<tr>
                    <td colspan=${props.canManage ? 2 : 1} class="text-center text-base-content/60 py-6">
                      No allowed queues configured.
                    </td>
                  </tr>`
                : sortedQueues.map(
                    row => html`
                      <tr class="hover">
                        <td class="font-mono text-sm">${row.queueName ?? '—'}</td>
                        ${props.canManage
                          ? html`<td>
                              <button
                                type="button"
                                class="btn btn-ghost btn-xs text-error"
                                @click=${() => void row.remove()}
                              >
                                Remove
                              </button>
                            </td>`
                          : nothing}
                      </tr>
                    `,
                  )}
            </tbody>
          </table>
        </div>
        <p class="text-sm text-base-content/50">${sortedQueues.length} queue(s)</p>
      </div>
    </section>
  `;
});
